import { useEffect, useRef } from 'react';
import { dateComplete, type DateIso } from './dates';

// Un bouton de souris est-il enfoncé ? (suivi une seule fois pour tous les champs). Valider une date à la SORTIE du champ réorganise la page (jours et séances
// ajoutés) : si la sortie vient d'un clic, la mise en page bouge entre l'appui et le relâchement et le clic est perdu. On attend donc le relâchement.
let pointeurEnfonce = false;
let enfonceDansCalendrier = false;   // l'appui a commencé dans la grille du calendrier : ses gestes s'appliquent dès l'appui, la date tapée doit donc passer AVANT eux
let enfonceSurNavigationMois = false;   // l'appui est sur ‹ › (mois précédent / suivant)
let suivi = false;
function suivrePointeur(): void {
  if (suivi || typeof document === 'undefined') return;
  suivi = true;
  document.addEventListener('pointerdown', (e) => {
    pointeurEnfonce = true;
    const cible = e.target as Element | null;
    // Une pastille ou « +N autres » agit au CLIC (elle ouvre une fenêtre) : comme un bouton, on attend le relâchement. Valider la date dès l'appui pouvait déplacer la page
    // (barre sur deux lignes) entre l'appui et le relâchement, et le clic n'atteignait plus le bouton.
    enfonceDansCalendrier = !!cible?.closest?.('.nd-grille') && !cible.closest('.nd-pastille, .nd-plus');
    enfonceSurNavigationMois = !!cible?.closest?.('.calendrier-nav');
  }, true);
  document.addEventListener('pointerup', () => { pointeurEnfonce = false; }, true);
  document.addEventListener('pointercancel', () => { pointeurEnfonce = false; }, true);
}
/**
 * Exécute `f` au relâchement du bouton de souris en cours (tout de suite s'il n'y en a pas, ou si l'appui a commencé dans le calendrier). Le relâchement vient AVANT
 * « mouseup » et « click » : ce que le clic fait ensuite (« Effacer les jours », ✕, un jour du calendrier…) s'applique APRÈS la date tapée, jamais avant — un
 * `setTimeout` la ferait passer après le clic et défaire ce qu'il vient de faire (constaté le 21/10 par la contre-vérification d'accessibilité).
 */
function aLaFinDuGeste(f: () => void): void {
  if (!pointeurEnfonce || enfonceDansCalendrier) { f(); return; }
  let fait = false;
  const fin = () => { if (fait) return; fait = true; document.removeEventListener('pointerup', fin, true); document.removeEventListener('pointercancel', fin, true); f(); };
  document.addEventListener('pointerup', fin, true);
  document.addEventListener('pointercancel', fin, true);
  setTimeout(fin, 1500);   // filet : un relâchement hors de la fenêtre ne doit pas laisser la date en suspens
}
/** Chrome fabrique l'année « 0026 » quand on tape « 26 » : on la lit comme 2026. */
const anneeComplete = (v: string): string => (/^00\d\d-/.test(v) ? `20${v.slice(2)}` : v);

/** Où va la personne en quittant le champ : l'élément qui reçoit le focus (Tab, clic), et si c'est un clic sur ‹ › du calendrier. */
export interface SortieChamp { suite: HTMLElement | null; navigationMois: boolean }

interface ChampDateProps {
  /** La date en vigueur (celle du calendrier) ; le champ la montre quand on ne tape pas dedans. */
  valeur: DateIso | '';
  min?: DateIso;
  'aria-label': string;
  'data-fid'?: string;
  /**
   * Une date COMPLÈTE est validée : choisie dans le sélecteur ou collée, tapée puis Entrée ou sortie du champ. `sortie` : elle l'est à la sortie du champ
   * (Tab, clic ailleurs) — la page ne doit alors pas déplacer le focus : la personne l'a déjà mis où elle voulait.
   */
  onValider: (v: DateIso, sortie: boolean, depuis?: SortieChamp) => void;
  /** Le champ est quitté avec une saisie vidée, incomplète ou impossible (31/04…) : il reprend la date en vigueur ; la page le dit. */
  onRefus?: () => void;
}

/**
 * Champ date « du calendrier » : en tapant « 26.11.2026 », le navigateur annonce une date valide à CHAQUE chiffre (jour 01, année 0002, 0020, 0202…).
 * Appliquer chacune détruit la période. Règle : une date tapée au clavier reste un brouillon, validée à la sortie du champ ou sur Entrée ; une date
 * choisie sans frappe (sélecteur, collage) est validée tout de suite. Le champ n'est jamais redessiné pendant la frappe : il n'est pas contrôlé,
 * on ne lui remet la date en vigueur que lorsqu'il n'a plus le curseur.
 */
export function ChampDate({ valeur, min, onValider, onRefus, ...rest }: ChampDateProps) {
  const ref = useRef<HTMLInputElement>(null);
  const tape = useRef(false);               // une touche a été frappée dans ce champ depuis la dernière date reçue
  const enAttente = useRef(false);          // une date tapée attend la fin d'un clic pour être validée : le champ la garde
  const derniere = useRef<string>(valeur);  // dernière date validée par ce champ
  useEffect(suivrePointeur, []);

  // Le champ montre la date en vigueur (celle du calendrier) : quand elle change ailleurs (glisser, fusion de deux périodes…) ou qu'une date validée
  // n'a pas été retenue telle quelle, il la reprend — sauf pendant la frappe (brouillon en cours), où on n'y touche jamais.
  useEffect(() => {
    const n = ref.current;
    if (n && !tape.current && !enAttente.current && n.value !== valeur) n.value = valeur;
    derniere.current = valeur;
  });

  const valider = (v: string, sortie = false, depuis?: SortieChamp) => {
    if (dateComplete(v) && v !== derniere.current) { derniere.current = v; onValider(v, sortie, depuis); }
  };

  return (
    <input
      ref={ref} type="date" className="form-input" defaultValue={valeur} min={min}
      aria-label={rest['aria-label']} data-fid={rest['data-fid']}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); valider(e.currentTarget.value); tape.current = false; }
        // Une frappe (chiffre, flèche d'un segment…) ouvre une saisie : la date reste un brouillon jusqu'à Entrée ou la sortie du champ. Tab, Maj, Échap et
        // les touches qui ouvrent le sélecteur (Alt + flèche, F4) n'en sont pas.
        else if (!['Tab', 'Shift', 'Escape', 'F4'].includes(e.key) && !e.altKey) tape.current = true;
      }}
      onPointerDown={() => { tape.current = false; }}
      // Le navigateur envoie « input » ET « change » à chaque chiffre : l'état « on tape » dure donc jusqu'à Entrée, la sortie du champ ou un clic.
      onChange={(e) => { if (!tape.current) valider(e.currentTarget.value); }}
      onBlur={(e) => {
        const champ = e.currentTarget;
        const depuis: SortieChamp = { suite: e.relatedTarget as HTMLElement | null, navigationMois: pointeurEnfonce && enfonceSurNavigationMois };
        const v = anneeComplete(champ.value);
        tape.current = false;
        if (!dateComplete(v)) {   // vide ou incomplet : on y remet la date en vigueur (ou rien)
          const avait = derniere.current !== '';
          champ.value = derniere.current;
          if (avait) onRefus?.();   // une période a toujours un début et une fin : on dit que la saisie n'a pas été prise
          return;
        }
        if (v !== champ.value) champ.value = v;
        enAttente.current = true;
        aLaFinDuGeste(() => { enAttente.current = false; valider(v, true, depuis); });
      }}
    />
  );
}
